import { tracking } from '@iwoca/frontend-tracking-library';
import { fetchLendingAPIJSON } from '@iwoca/lapi-client/lendingAPI';
import Cookies from 'js-cookie';

import { runSplitTest } from './runSplitTest';
import { SPLIT_TESTS } from './SPLIT_TESTS';
import { useGetContext } from '../api/lendingApi/lendingApiQueries';
import { getSplitTestFromContext } from '../hooks/useSplitTestContext';
import { useStorybookSplitTestControls } from '../storybook/splitTest';
import { TGetContext } from '../types';
import { getRuntimeEnvironment } from '../utils/getRuntimeEnvironment';

export type TBranch = { branchName: string; testPercentage: number };
export type TAnonymousSplitTest = (typeof SPLIT_TESTS)['tests'][number];
type TSplitTestData = {
  test_name: string;
  branch_name: string;
  created?: string;
  fraction?: number;
  random_value?: number;
};

export const ADD_TO_SPLIT_TEST_URL = '/api/split_tests/add_to_split_test/';

export const assignAnonymousSplitTest = async (data: TSplitTestData) => {
  if (Cookies.get(`iw-${data['test_name']}`)) return;

  const options =
    getRuntimeEnvironment() === 'production'
      ? { domain: window.location.hostname.replace('www.', ''), expires: 7665 }
      : {};

  Cookies.set(`iw-${data['test_name']}`, JSON.stringify(data), options);

  tracking.ampli.addedToAnonymousSplitTest({
    Branch: data.branch_name,
    'Test name': data.test_name,
    UID: Cookies.get('uid') || '',
  });

  await fetchLendingAPIJSON(ADD_TO_SPLIT_TEST_URL, {
    method: 'post',
    body: data,
  });
};

const getSplitTestBranch = (
  splitTest: TAnonymousSplitTest,
  context: TGetContext['data'],
): TSplitTestData => {
  const testName = splitTest.name;
  const splitTestCookie = Cookies.get(`iw-${testName}`);

  const testFromContext = getSplitTestFromContext(testName, context);

  if (testFromContext) {
    return {
      test_name: testName,
      branch_name: testFromContext[testName] ? 'INCLUDE' : 'EXCLUDE',
    };
  }

  if (splitTestCookie) {
    return JSON.parse(splitTestCookie);
  }

  const randomNumber = Math.random();
  const activeBranch = runSplitTest({
    splitTest,
    randomNumber,
    threshold: splitTest.fraction,
  });

  const currentDateTime = new Date();

  const splitTestData = {
    test_name: testName,
    branch_name: activeBranch.branchName,
    created: currentDateTime.toISOString(),
    fraction: activeBranch.testPercentage,
    random_value: randomNumber,
  };

  void assignAnonymousSplitTest(splitTestData);

  return splitTestData;
};

export const useAnonymousSplitTests = ({
  testFilter,
  enabled = true,
}: {
  testFilter?: TAnonymousSplitTest['name'];
  enabled?: boolean;
} = {}) => {
  const { context } = useGetContext();
  const storybookSplitTestControls = useStorybookSplitTestControls();

  if (!enabled) return;

  if (storybookSplitTestControls.length > 0) {
    return storybookSplitTestControls.filter((test) => {
      if (testFilter && test.test_name !== testFilter) return false;
      return true;
    });
  }

  const tests = SPLIT_TESTS.tests
    .filter((test) => (testFilter && test.name !== testFilter ? false : true))
    .map((test) => getSplitTestBranch(test, context));

  return tests;
};
